import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';

export const AdminWithdrawalsPage = () => {
  const [status, setStatus] = useState('pending');
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [txid, setTxid] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { load(); }, [status]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getWithdrawals(status);
      // Handle both array and object responses
      setWithdrawals(Array.isArray(data) ? data : (data.items || data.withdrawals || []));
    } catch (error) {
      toast.error('Failed to load');
      setWithdrawals([]);
    } finally { setLoading(false); }
  };

  const openComplete = (w) => {
    setSelected(w);
    setTxid('');
    setNotes('');
  };

  const submitComplete = async () => {
    if (!txid) { toast.error('Please enter blockchain TXID'); return; }
    try {
      await adminAPI.completeWithdrawal(selected.id, txid, notes);
      toast.success('Withdrawal marked complete');
      setSelected(null);
      load();
    } catch (error) {
      toast.error('Failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-withdrawals-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Withdrawals</h1>
          <p className="mt-2 text-muted-foreground">Process withdrawal requests</p>
        </div>

        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="withdrawals-pending-tab">Pending</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          <TabsContent value={status} className="mt-6">
            <Card>
              <CardContent className="pt-6">
                {loading ? <p className="text-center py-8">Loading...</p> : withdrawals.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No {status} withdrawals</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Currency</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Network</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Status</TableHead>
                        {status === 'pending' && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {withdrawals.map((w) => (
                        <TableRow key={w.id}>
                          <TableCell>{formatDate(w.created_at)}</TableCell>
                          <TableCell className="font-mono text-xs">{w.from_user_id?.substring(0, 12)}...</TableCell>
                          <TableCell className="font-semibold">{w.currency}</TableCell>
                          <TableCell>{w.amount}</TableCell>
                          <TableCell>{w.metadata?.network || '-'}</TableCell>
                          <TableCell className="font-mono text-xs truncate max-w-[200px]">{w.metadata?.wallet_address || '-'}</TableCell>
                          <TableCell>
                            <Badge className={w.status === 'completed' ? 'bg-green-600' : 'bg-yellow-600'}>{w.status}</Badge>
                          </TableCell>
                          {status === 'pending' && (
                            <TableCell className="text-right">
                              <Button size="sm" onClick={() => openComplete(w)} data-testid={`complete-withdrawal-${w.id}`}>
                                Mark Complete
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Withdrawal</DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-muted space-y-1 text-sm">
                  <p><span className="text-muted-foreground">Amount:</span> {selected.amount} {selected.currency}</p>
                  <p><span className="text-muted-foreground">To:</span> <span className="font-mono">{selected.metadata?.wallet_address}</span></p>
                  <p><span className="text-muted-foreground">Network:</span> {selected.metadata?.network}</p>
                </div>
                <div className="space-y-2">
                  <Label>Blockchain TXID</Label>
                  <Input value={txid} onChange={(e) => setTxid(e.target.value)} placeholder="Transaction hash" data-testid="txid-input" />
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
              <Button onClick={submitComplete} data-testid="submit-complete-withdrawal">Mark Complete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
